import React, { useEffect, useState } from 'react';
import { IAxiosGetResponse, IServiceDto, ISyncType, ITransactionRouteDto,
  IServiceAggregatorTransactionParameterDto,
  ITransformationsTransactionParameterDto,
  ITransactionParamsDifferenceDto,
  ITransactionParameterDto,
  IParamValueType,
  IParamType} from '../../interfaces/IUtilities';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../modules/layout/components/ToastContext';
import { useNavigate, useParams } from 'react-router-dom';
import { getById, updateById } from '../_request';
import Loader from '../../modules/layout/components/Loader';
import CapitalizeFirstLetter from '../../helpers/Utils';
import ConfirmModal from '../../modules/layout/components/ConfirmModal';
import AppBreadcrumb from '../../modules/layout/components/AppBreadcrumb';
import BackButton from '../../modules/layout/components/BackButton';
import { ArrowClockwise } from 'react-bootstrap-icons';
import { ParamTypeText, ParamValueTypeText } from '../../helpers/EnumeratorHelper';
import { Transactions_Approve } from '../../constants/constants';
import { userHasRole } from '../../../setup/auth/authService';


const serviceSAFields: (keyof IServiceAggregatorTransactionParameterDto)[] = [
  "id", "transactionId", "paramType", "paramName", "paramValue", "paramValueType", "isDeleted"
];

const serviceTRFields: (keyof ITransformationsTransactionParameterDto)[] = [
  "id", "transactionId", "paramType", "paramName", "paramValue", "paramValueType", "isDeleted"
]; //"createdAt", "modifiedAt"

const trFieldKeyMap: Record<keyof ITransformationsTransactionParameterDto, string> = {
  id: "id",
  transactionId: "tr-transactionId",
  paramType: "tr-paramType",
  paramName: "tr-paramName",
  paramValue: "tr-paramValue",
  paramValueType: "tr-paramValueType",
  createdAt: '',
  modifiedAt: '',
  isDeleted: "tr-isDeleted"
};

const TransactionParameterDataDifferences = () => { 
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const { serviceid, transactionid, id } = useParams<{ serviceid: string; transactionid: string; id: string }>();
  const [service, setService] = useState({} as IServiceDto);
  const [transaction, setTransaction] = useState({} as ITransactionRouteDto);
  const [diffSAData, setDiffSAData] = useState({} as ITransactionParamsDifferenceDto);
  const [diffTRData, setDiffTRData] = useState({} as ITransactionParamsDifferenceDto);
  const [showModal, setShowModal] = useState(false);
  const [enableSASync, setEnableSASync] = useState(false);  
  const [enableTRSync, setEnableTRSync] = useState(false);  
  const [titleSync, setTitleSync] = useState('');  
  const [syncBtn, setSyncBtn] = useState('');
  const [isTransactionApproved, setIsTransactionApproved] = useState(false);
  const [isApprover, setIsApprover] = useState(false);

  const fetchServiceData = async () => {
      try {
        let result = await getById("/api/services/", serviceid!);
        let respStatus = result?.status || result?.response.status;

        if (respStatus === 200) {
            setService(result.data as IServiceDto);
            setLoading(false);
        }
        else if (respStatus === 403) {
            navigate("/error/403");
        }
        else if (respStatus === 401) {
            navigate("/auth/login");
        }
        else if (respStatus === 500) {
            navigate("/error/500");
        }
        else {
          showToast(t('StatusCode', { code: String(respStatus) }), 'error');
          throw new Error(t('StatusCode', { code: String(respStatus) }));
        }
      } catch (error) {
          setLoading(false);
          console.log(t('ConnectionError', { error: String(error) }));
          showToast(t('ConnectionError', { error: String(error) }), 'error');
          navigate(`/services/${serviceid}/transactions/${transactionid}/transaction-parameters/list`);
      }
  };

  const fetchTransactionData = async (): Promise<boolean> => {
    try {
      let result = await getById("/api/transactionroutes/", transactionid!);
      let respStatus = result?.status || result?.response.status;

      if (respStatus === 200) {
        let data = result.data as ITransactionRouteDto;
        setTransaction(data);
        
        const isApproved = data.isApproved; //Approved condition
        setIsTransactionApproved(isApproved);

        setLoading(false);
        return isApproved;
      }
      else if (respStatus === 403) {
          navigate("/error/403");
      }
      else if (respStatus === 401) {
          navigate("/auth/login");
      }
      else if (respStatus === 500) {
          navigate("/error/500");
      }
      else {
        showToast(t('StatusCode', { code: String(respStatus) }), 'error');
        throw new Error(t('StatusCode', { code: String(respStatus) }));
      }

      return false;
    } catch (error) {
      setLoading(false);
      console.log(t('ConnectionError', { error: String(error) }));
      showToast(t('ConnectionError', { error: String(error) }), 'error');
      navigate(`/services/${serviceid}/transactions/${transactionid}/transaction-parameters/list`);
      return false;
    }
  };

  const fetchSAData = async (isTranscationApproved: boolean, hasRole: boolean) => {
    try {
      let result = await getById('/api/transactionparameters/comparesatransactionparams/', id!);
      let respStatus = result?.status || result?.response.status;

      if (respStatus === 200) {
        let data = result.data as ITransactionParamsDifferenceDto;
        setDiffSAData(data);

        //isTranscationApproved && 
        let saSync = (hasRole && (data?.diffColumnsList?.some(col => !col.startsWith("tr-")) || data?.serviceAggregatorTransactionParam === null)) ?? false;   
        setEnableSASync(saSync); 
        setLoading(false);
      }
      else if (respStatus === 403) {
          navigate("/error/403");
      }
      else if (respStatus === 401) {
          navigate("/auth/login");
      }
      else if (respStatus === 500) {
          navigate("/error/500");
      }
      else{
        showToast(t('StatusCode', { code: String(respStatus) }), 'error');
        throw new Error(t('StatusCode', { code: String(respStatus) }));
      }
    } catch (error) {
      setLoading(false);
      console.log(t('ConnectionError', { error: String(error) }));
      showToast(t('ConnectionError', { error: String(error) }), 'error');
      navigate(`/services/${serviceid}/transactions/${transactionid}/transaction-parameters/list`);
    }
  };

  const fetchTRData = async (isTranscationApproved: boolean, hasRole: boolean) => {
    try {
      let result = await getById('/api/transactionparameters/comparetrtransactionparams/', id!);
      let respStatus = result?.status || result?.response.status;

      if (respStatus === 200) {
        let data = result.data as ITransactionParamsDifferenceDto;
        setDiffTRData(data);

        //isTranscationApproved && 
        let trSync = (hasRole && (data?.diffColumnsList?.some(col => col.startsWith("tr-")) || data?.transformationsTransactionParam === null)) ?? false;
        setEnableTRSync(trSync);
        setLoading(false);
      }
      else if (respStatus === 403) {
          navigate("/error/403");
      }
      else if (respStatus === 401) {
          navigate("/auth/login");
      }
      else if (respStatus === 500) {
          navigate("/error/500");
      }
      else{
        showToast(t('StatusCode', { code: String(respStatus) }), 'error');
        throw new Error(t('StatusCode', { code: String(respStatus) }));
      }
    } catch (error) {
      setLoading(false);
      console.log(t('ConnectionError', { error: String(error) }));
      showToast(t('ConnectionError', { error: String(error) }), 'error');
      navigate(`/services/${serviceid}/transactions/${transactionid}/transaction-parameters/list`);
    }
  };

  useEffect(() => {
    const fetchAll = async () => { 
      await fetchServiceData(); 
      const hasRole = await checkUserRole();
      const isTranscationApproved = await fetchTransactionData();
      await Promise.all([fetchSAData(isTranscationApproved, hasRole), fetchTRData(isTranscationApproved, hasRole)]);
    };
    fetchAll();
  }, []);

  const getFormattedValue = (field: string, value: any): string => {
    switch (field) {
      case 'paramType': return ParamTypeText[value as IParamType];
      case 'paramValueType': return ParamValueTypeText[value as IParamValueType];
      default: return String(value ?? '');
    }
  };

  const handleSyncSA = () => {
    setShowModal(true);
    setTitleSync(t('SyncIn', { database: "Service Aggregator" }));
    setSyncBtn(ISyncType.ServiceAggregator);
  };

  const handleSyncTR = () => {
    setShowModal(true);
    setTitleSync(t('SyncIn', { database: "Transformations" }));
    setSyncBtn(ISyncType.Transformations);
  };

  const handleConfirmSync = async() => {
    setLoading(true);
    try {
      let result = {} as any;
      let respStatus;

      if (syncBtn === ISyncType.ServiceAggregator) {
        try {
          let resultSAService = await getById('/api/services/checksaservice/', serviceid!);
          let respStatusSAService = resultSAService?.status || resultSAService?.response.status;
  
          if (respStatusSAService === 200) {
            showToast(t('SyncServiceInDatabaseAddSuccess', { database: "Service Aggregator" }), 'success');
            
            try {
              let resultSATransaction = await getById('/api/transactionroutes/checksatransaction/', transactionid!);
              let respStatusSATransaction = resultSATransaction?.status || resultSATransaction?.response.status;
      
              if (respStatusSATransaction === 200) {
                showToast(t('SyncTransactionInDatabaseAddSuccess', { database: "Service Aggregator" }), 'success');
              }
              else if (respStatusSATransaction === 403) {
                navigate("/error/403");
              }
              else if (respStatusSATransaction === 401) {
                navigate("/auth/login");
              }
              else if (respStatusSATransaction === 500) {
                navigate("/error/500");
              }
              else{
                showToast(t('StatusCode', { code: String(respStatusSATransaction) }), 'error');
                throw new Error(t('StatusCode', { code: String(respStatusSATransaction) }));
              }
            } catch (error) {
              setLoading(false);
              console.log(t('ConnectionError', { error: String(error) }));
              showToast(t('ConnectionError', { error: String(error) }), 'error');
              navigate(`/services/${serviceid}/transactions/${transactionid}/transaction-parameters/list`);
            }
          }
          else if (respStatusSAService === 403) {
              navigate("/error/403");
          }
          else if (respStatusSAService === 401) {
              navigate("/auth/login");
          }
          else if (respStatusSAService === 500) {
              navigate("/error/500");
          }
          else{
            showToast(t('StatusCode', { code: String(respStatusSAService) }), 'error');
            throw new Error(t('StatusCode', { code: String(respStatusSAService) }));
          }
        } catch (error) {
          setLoading(false);
          console.log(t('ConnectionError', { error: String(error) }));
          showToast(t('ConnectionError', { error: String(error) }), 'error');
          navigate(`/services/${serviceid}/transactions/${transactionid}/transaction-parameters/list`);
        }

        result = await updateById('/api/transactionparameters/syncsatransactionparams', id!) as IAxiosGetResponse;
        respStatus = result?.status || result?.response.status;
      }
      else if (syncBtn === ISyncType.Transformations) {
        try {
          let resultTRService = await getById('/api/services/checktrservice/', serviceid!);
          let respStatusTRService = resultTRService?.status || resultTRService?.response.status;
  
          if (respStatusTRService === 200) {
            showToast(t('SyncServiceInDatabaseAddSuccess', { database: "Transformations" }), 'success');
            
            try {
              let resultTRTransaction = await getById('/api/transactionroutes/checktrtransaction/', transactionid!);
              let respStatusTRTransaction = resultTRTransaction?.status || resultTRTransaction?.response.status;
      
              if (respStatusTRTransaction === 200) {
                showToast(t('SyncTransactionInDatabaseAddSuccess', { database: "Transformations" }), 'success');
              }
              else if (respStatusTRTransaction === 403) {
                navigate("/error/403");
              }
              else if (respStatusTRTransaction === 401) {
                navigate("/auth/login");
              }
              else if (respStatusTRTransaction === 500) {
                navigate("/error/500");
              }
              else{
                showToast(t('StatusCode', { code: String(respStatusTRTransaction) }), 'error');
                throw new Error(t('StatusCode', { code: String(respStatusTRTransaction) }));
              }
            } catch (error) {
              setLoading(false);
              console.log(t('ConnectionError', { error: String(error) }));
              showToast(t('ConnectionError', { error: String(error) }), 'error');
              navigate(`/services/${serviceid}/transactions/${transactionid}/transaction-parameters/list`);
            }
          }
          else if (respStatusTRService === 403) {
              navigate("/error/403");
          }
          else if (respStatusTRService === 401) {
              navigate("/auth/login");
          }
          else if (respStatusTRService === 500) {
              navigate("/error/500");
          }
          else{
            showToast(t('StatusCode', { code: String(respStatusTRService) }), 'error');
            throw new Error(t('StatusCode', { code: String(respStatusTRService) }));
          }
        } catch (error) {
          setLoading(false);
          console.log(t('ConnectionError', { error: String(error) }));
          showToast(t('ConnectionError', { error: String(error) }), 'error');
          navigate(`/services/${serviceid}/transactions/${transactionid}/transaction-parameters/list`);
        }

        result = await updateById('/api/transactionparameters/synctrtransactionparams', id!) as IAxiosGetResponse;
        respStatus = result?.status || result?.response.status;
      }

      if (respStatus === 200) {
        let isSync = result.data as boolean;
        setLoading(false);

        if (syncBtn === ISyncType.ServiceAggregator) {
          await fetchSAData(isTransactionApproved, isApprover);
        }
        else if (syncBtn === ISyncType.Transformations) {
          await fetchTRData(isTransactionApproved, isApprover);
        }

        if(isSync) {
          if (syncBtn === ISyncType.ServiceAggregator) {
            showToast(t('SyncInDatabaseAddSuccess', { database: "Service Aggregator" }), 'success');
          }
          else {
            showToast(t('SyncInDatabaseAddSuccess', { database: "Transformations" }), 'success');
          }
        }
        else{
          if (syncBtn === ISyncType.ServiceAggregator) {
            showToast(t('SyncInDatabaseAddFail', { database: "Service Aggregator" }), 'error');
          }
          else {
            showToast(t('SyncInDatabaseAddFail', { database: "Transformations" }), 'error');
          }
        }
      }
      else if (respStatus === 400 && result.data?.errors) {
          setLoading(false);
          for (const key in result.data.errors) {
              showToast(t(key), 'error');
          }
      }
      else if (respStatus === 403) {
          navigate("/error/403");
      }
      else if (respStatus === 401) {
          navigate("/auth/login");
      }
      else if (respStatus === 500) {
          navigate("/error/500");
      }
      else {
        showToast(t('StatusCode', { code: String(respStatus) }), 'error');
        throw new Error(t('StatusCode', { code: String(respStatus) }));
      }
    } catch (error) {
      setLoading(false);
      console.log(t('ConnectionError', { error: String(error) }));
      showToast(t('ConnectionError', { error: String(error) }), 'error');
      navigate(`/services/${serviceid}/transactions/${transactionid}/transaction-parameters/list`);
    }

    setShowModal(false);
  };
  
  const handleCancelSync = () => {
    setShowModal(false);
  };

  const handleRefreshClick = async (btnName: string) => {
    setLoading(true);
    try {
      if (btnName === ISyncType.ServiceAggregator) {
        await fetchSAData(isTransactionApproved, isApprover);
        showToast(t('DatabaseTableRefreshed', { database: "Service Aggregator" }), 'success');
      }
      else if (btnName === ISyncType.Transformations) {
        await fetchTRData(isTransactionApproved, isApprover);
        showToast(t('DatabaseTableRefreshed', { database: "Transformations" }), 'success');
      }
    } catch (error) {
      setLoading(false);
      console.log(t('RefreshError'), error);
      showToast(`${t('FailedToRefresh')} ${btnName === ISyncType.ServiceAggregator ? 'Service Aggregator' : 'Transformations'} ${t('Table')}`, 'error');
    }
    setLoading(false);
  };

  const checkUserRole = async (): Promise<boolean> => {
    const hasRole = await userHasRole(Transactions_Approve);
    setIsApprover(hasRole);
    return hasRole;
  }


  return (
    <div className="container mt-4 gg-tranparamsdiff">
      <AppBreadcrumb 
        items={[
            { title: t('Home'), href: "/" },
            { title: t('Services'), href: "/services/list" },
            { title: `${service ? service.name : ""}: ${t('TransactionRoutes')}`, href: `/services/${serviceid}/transactions/list` },
            { title: `${transaction ? transaction.routeLabel : ""}: ${t('TransactionParameters')}`, href: `/services/${serviceid}/transactions/${transactionid}/transaction-parameters/list` },
            { title: `${diffSAData.adminWebTransactionParam ? diffSAData.adminWebTransactionParam?.paramName : ""}: ${t('TransactionParametersDataDifference')}` } // Active page (no href)
        ]} />
        
      <h3>{t('TransactionParametersDataDifferences')}</h3>

      {loading && (<Loader />)}

      {(diffSAData || diffTRData) ?
        <div className="gg-tranparams-diff">
          <h5>{t('RecordsFromDatabases')}</h5>

            <div key={diffSAData.id} className="my-3 border rounded p-3 shadow">
              <h4 className="text-start font-bold mb-2">Transactions Parameters ID: {diffSAData.id}</h4>

              <div className="row gg-tranparams-sa-diff">
                <div className="col">
                  <div className="table-responsive">
                    <table className="table table-bordered text-start w-full">
                      <thead>
                        <tr>
                          <th className="p-1 text-left">{t('Source')}</th>
                          {serviceSAFields.map((field) => (
                            <th key={field} className="p-1 text-left">
                              <CapitalizeFirstLetter text={field} />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {['adminWebTransactionParam', 'serviceAggregatorTransactionParam'].map((sourceKey) => {
                          const sourceData =
                            sourceKey === 'adminWebTransactionParam'
                              ? (diffSAData.adminWebTransactionParam as ITransactionParameterDto)
                              : (diffSAData.serviceAggregatorTransactionParam as IServiceAggregatorTransactionParameterDto);

                          return (
                            <tr key={`${diffSAData.id}-${sourceKey}`}>
                              <td className={`p-1 border font-semibold ${diffSAData.diffColumnsList?.some(col => !col.startsWith("tr-")) ? 'text-danger' : ''}`}>
                                {sourceKey === 'adminWebTransactionParam' ? 'Admin Web' : 'Service Aggregator'} {t('Database')}
                              </td>

                              {serviceSAFields.map((field) => {
                                let value = getFormattedValue(field, sourceData?.[field as keyof typeof sourceData]);
                                const isDifferent = diffSAData.diffColumnsList?.includes(field);

                                switch (diffSAData.differenceType) {
                                  case 0:
                                    return (
                                      <td
                                        key={field}
                                        className="p-1 border text-success"
                                      >
                                        {String(value)}
                                      </td> 
                                    );

                                  case 1:
                                    return diffSAData.serviceAggregatorTransactionParam ? (
                                      <td
                                        key={field}
                                        className={`p-1 border ${isDifferent ? 'text-danger fw-bold' : 'text-success'}`}
                                      >
                                        {value}
                                      </td>
                                    ) : <td
                                        key={field}
                                        className={`p-1 border ${sourceKey === 'adminWebTransactionParam' ? 'text-primary' : ''}`}
                                      >
                                        {sourceKey === 'serviceAggregatorTransactionParam' ? '-' : value}
                                      </td>;

                                  default:
                                    return (
                                      <td
                                        key={field}
                                        className="p-1 border text-primary"
                                      >
                                        {'-'}
                                      </td> 
                                    );
                                }

                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="row my-2">
                <div className="col d-flex justify-content-end align-items-end">
                  <button title={t('Refresh')} className="btn btn-success me-2 gg-btn-refresh" onClick={() => handleRefreshClick(ISyncType.ServiceAggregator)}>
                    <ArrowClockwise size={20}/>
                  </button>
                  <button type="button" disabled={!enableSASync} className="btn btn-warning"
                    onClick={handleSyncSA}>{t('SyncIn', { database: "Service Aggregator" })}</button>
                </div>
              </div>

              <div className="row gg-tranparams-tr-diff">
                <div className="col">
                  <div className="table-responsive">
                    <table className="table table-bordered text-start w-full">
                      <thead>
                        <tr>
                          <th className="p-1 text-left">{t('Source')}</th>
                          {serviceTRFields.map((field) => (
                            <th key={field} className="p-1 text-left">
                              <CapitalizeFirstLetter text={field} />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {['adminWebTransactionParam', 'transformationsTransactionParam'].map((sourceKey) => {
                          const sourceData =
                            sourceKey === 'adminWebTransactionParam'
                              ? (diffTRData.adminWebTransactionParam as ITransactionParameterDto)
                              : (diffTRData.transformationsTransactionParam as ITransformationsTransactionParameterDto);

                          return (
                            <tr key={`${diffTRData.id}-${sourceKey}`}>
                              <td className={`p-1 border font-semibold ${diffTRData.diffColumnsList?.some(col => col.startsWith("tr-")) ? 'text-danger' : ''}`}>
                                {sourceKey === 'adminWebTransactionParam' ? 'Admin Web' : 'Transformations'} {t('Database')}
                              </td>

                              {serviceTRFields.map((field) => {
                                const backendKey = trFieldKeyMap[field];
                                let value = getFormattedValue(field, sourceData?.[field as keyof typeof sourceData]);
                                const isDifferent = diffTRData.diffColumnsList?.includes(backendKey);

                                switch (diffTRData.differenceType) {
                                  case 0:
                                    return (
                                      <td
                                        key={field}
                                        className="p-1 border text-success"
                                      >
                                        {String(value)}
                                      </td> 
                                    );

                                  case 1:
                                    return diffTRData.transformationsTransactionParam ? (
                                      <td
                                        key={field}
                                        className={`p-1 border ${isDifferent ? 'text-danger fw-bold' : 'text-success'}`}
                                      >
                                        {value}
                                      </td>
                                    ) : <td
                                        key={field}
                                        className={`p-1 border ${sourceKey === 'adminWebTransactionParam' ? 'text-primary' : ''}`}
                                      >
                                        {sourceKey === 'transformationsTransactionParam' ? '-' : value}
                                      </td>;

                                  default:
                                    return (
                                      <td
                                        key={field}
                                        className="p-1 border text-primary"
                                      >
                                        {'-'}
                                      </td> 
                                    );
                                }

                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
             
              <div className="row my-2">
                <div className="col d-flex justify-content-end align-items-end">
                  <button title={t('Refresh')} className="btn btn-success me-2 gg-btn-refresh" onClick={() => handleRefreshClick(ISyncType.Transformations)}>
                    <ArrowClockwise size={20}/>
                  </button>
                  <button type="button" disabled={!enableTRSync} className="btn btn-warning"
                    onClick={handleSyncTR}>{t('SyncIn', { database: "Transformations" })}</button>
                </div>
              </div>

            </div>
        </div>
      : <p className="text-warning">{t('NoDataDifferencesFoundBetweenAWAndDatabases')}</p>}

      <ConfirmModal
        show={showModal}
        title={titleSync}
        message={t('SyncWithDatabase')}
        onConfirm={handleConfirmSync}
        onCancel={handleCancelSync}
        buttonClass="warning"
        loading={loading}
      />
      
      <div className="row">
        <div className="col d-flex justify-content-between align-items-end">
          <BackButton goTo={`/services/${serviceid}/transactions/${transactionid}/transaction-parameters/list`} />
        </div>
      </div>

    </div>
  );
};

export default TransactionParameterDataDifferences;
